import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { useDisplay } from "@/context/DisplayContext";
import { useNotifications } from "@/context/NotificationsContext";
import { Button } from "@/components/ui/button";
import BetSlip from "@/components/BetSlip";
import { LayoutDashboard, Moon, Sun, LogOut, Trophy, Users, Goal, Star, TrendingUp, Activity, ShieldCheck, Dices, Percent, SlidersHorizontal, MessagesSquare, Bell, Download, Menu } from "lucide-react";
import { useState } from "react";
import { cn } from "@/lib/utils";

const links = [
  { to: "/", label: "Home", icon: LayoutDashboard, testid: "nav-home", end: true },
  { to: "/tournaments", label: "Competitions", icon: Goal, testid: "nav-tournaments", end: false },
  { to: "/best-bets", label: "Best Bets", icon: Star, testid: "nav-best-bets", end: false },
  { to: "/value-finder", label: "Value", icon: TrendingUp, testid: "nav-value", end: false },
  { to: "/track-record", label: "Record", icon: Activity, testid: "nav-track-record", end: false, adminOnly: true },
  { to: "/admin/accounts", label: "Accounts", icon: ShieldCheck, testid: "nav-accounts", end: false, adminOnly: true },
  { to: "/admin/model-tuning", label: "Tuning", icon: SlidersHorizontal, testid: "nav-model-tuning", end: false, adminOnly: true },
  { to: "/groups", label: "Groups", icon: Users, testid: "nav-groups", end: false },
  { to: "/mates", label: "Mates", icon: MessagesSquare, testid: "nav-mates", end: false },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const { format, toggle: toggleFormat } = useDisplay();
  const { unread, canInstall, promptInstall } = useNotifications();
  const navigate = useNavigate();

  const visibleLinks = links.filter((l) => !l.adminOnly || user?.role === "admin");
  const [moreOpen, setMoreOpen] = useState(false);
  // mobile: keep the bar to 4 primary items + a "More" sheet for the rest
  const mobilePrimary = visibleLinks.length <= 5 ? visibleLinks : visibleLinks.slice(0, 4);
  const mobileExtra = visibleLinks.length <= 5 ? [] : visibleLinks.slice(4);

  const handleLogout = async () => {
    await logout();
    navigate("/auth");
  };

  return (
    <div className="min-h-screen bg-background relative">
      <header className="sticky top-0 z-50 glass" data-testid="app-header" style={{ paddingTop: "env(safe-area-inset-top)" }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-2xl bg-primary flex items-center justify-center shadow-lg shadow-primary/30">
              <Trophy className="h-5 w-5 text-primary-foreground" />
            </div>
            <span className="font-display font-extrabold text-lg tracking-tight hidden sm:block">
              PitchLedger
            </span>
          </div>

          <nav className="hidden sm:flex items-center gap-1 bg-secondary/60 rounded-full p-1">
            {visibleLinks.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                data-testid={l.testid}
                title={l.label}
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-2 px-3 py-2 rounded-full text-sm font-medium transition-colors duration-200 whitespace-nowrap",
                    isActive
                      ? "bg-card text-foreground shadow-sm"
                      : "text-muted-foreground hover:text-foreground"
                  )
                }
              >
                <l.icon className="h-4 w-4 shrink-0" />
                <span className="hidden xl:inline">{l.label}</span>
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            {canInstall && (
              <Button
                variant="ghost"
                size="sm"
                onClick={promptInstall}
                data-testid="install-app-btn"
                className="rounded-full text-xs font-semibold gap-1.5 px-3 hidden sm:flex"
                title="Add PitchLedger to your home screen"
              >
                <Download className="h-4 w-4" /> Install
              </Button>
            )}
            <button
              onClick={() => navigate("/mates")}
              data-testid="nav-bell"
              className="relative rounded-full h-9 w-9 flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
              title="Messages from your mates"
            >
              <Bell className="h-5 w-5" />
              {unread > 0 && (
                <span data-testid="bell-unread" className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-1 rounded-full bg-[#FF375F] text-white text-[10px] font-bold flex items-center justify-center">
                  {unread > 9 ? "9+" : unread}
                </span>
              )}
            </button>
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleFormat}
              data-testid="prob-format-toggle"
              className="rounded-full text-xs font-semibold gap-1.5 px-3"
              title="Switch between chance (X in Y) and percentage"
            >
              {format === "chance" ? <Dices className="h-4 w-4" /> : <Percent className="h-4 w-4" />}
              <span className="hidden sm:inline">{format === "chance" ? "Chance" : "Percent"}</span>
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={toggle}
              data-testid="theme-toggle"
              className="rounded-full"
            >
              {theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleLogout}
              data-testid="logout-button"
              className="rounded-full text-muted-foreground hover:text-destructive"
              title={user?.email}
            >
              <LogOut className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 pb-[calc(6rem+env(safe-area-inset-bottom))] sm:pb-8">
        <Outlet />
      </main>

      <BetSlip />

      {/* Mobile bottom navigation */}
      <nav
        className="sm:hidden fixed bottom-0 inset-x-0 z-50 glass border-t border-border pb-[env(safe-area-inset-bottom)]"
        data-testid="mobile-bottom-nav"
      >
        <div className="flex items-stretch justify-around px-1 py-1.5">
          {mobilePrimary.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              data-testid={`${l.testid}-mobile`}
              className={({ isActive }) =>
                cn(
                  "flex flex-col items-center justify-center gap-0.5 flex-1 py-1.5 rounded-xl text-[10px] font-medium transition-colors",
                  isActive ? "text-primary" : "text-muted-foreground"
                )
              }
            >
              <l.icon className="h-5 w-5" />
              <span className="leading-none truncate max-w-full">{l.label}</span>
            </NavLink>
          ))}
          {mobileExtra.length > 0 && (
            <button
              onClick={() => setMoreOpen(true)}
              data-testid="nav-more-mobile"
              className="flex flex-col items-center justify-center gap-0.5 flex-1 py-1.5 rounded-xl text-[10px] font-medium text-muted-foreground"
            >
              <Menu className="h-5 w-5" />
              <span className="leading-none">More</span>
            </button>
          )}
        </div>
      </nav>

      {moreOpen && (
        <div className="sm:hidden fixed inset-0 z-[60] bg-black/40 flex items-end" data-testid="more-sheet" onClick={() => setMoreOpen(false)}>
          <div className="glass w-full rounded-t-3xl p-4 pb-[calc(2rem+env(safe-area-inset-bottom))]" onClick={(e) => e.stopPropagation()}>
            <div className="h-1 w-10 bg-muted-foreground/30 rounded-full mx-auto mb-4" />
            <div className="grid grid-cols-4 gap-3">
              {mobileExtra.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.end}
                  data-testid={`${l.testid}-more`}
                  onClick={() => setMoreOpen(false)}
                  className={({ isActive }) =>
                    cn(
                      "flex flex-col items-center justify-center gap-1.5 py-3 rounded-2xl text-[11px] font-medium",
                      isActive ? "bg-secondary text-primary" : "text-muted-foreground hover:bg-secondary/60"
                    )
                  }
                >
                  <l.icon className="h-5 w-5" />
                  <span className="leading-none text-center">{l.label}</span>
                </NavLink>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
